import { findNodeById } from "./NodeUtils";


// Generate IDs
export function generateId(prefix) {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

// Placeholder element factory
export function createPlaceholderElement() {
  return {
    id: generateId("el"),
    type: "placeholder",
    styleRole: "placeholder",
    content: "Add element",
    visible: true,
    styles: { default: {}, theme: {}, custom: {} }
  };
}

export function addContainer(page, sectionId) {
  const newPage = JSON.parse(JSON.stringify(page));
  const sec = newPage.sections.find(s => s.id === sectionId);
  if (!sec) return page;

  const newContainer = {
    id: generateId("con"),
    layoutRole: "column",
    order: sec.contents.length,
    visible: true,
    cards: [
      {
        id: generateId("crd"),
        componentType: "genericCard",
        order: 0,
        visible: true,
        elements: [createPlaceholderElement()]
      }
    ]
  };

  sec.contents.push(newContainer);
  return newPage;
}

export function addCard(page, sectionId, containerId) {
  const newPage = JSON.parse(JSON.stringify(page));

  const sec = newPage.sections.find(s => s.id === sectionId);
  if (!sec) return page;

  const con = sec.contents.find(c => c.id === containerId);
  if (!con) return page;

  con.cards.push({
    id: generateId("crd"),
    componentType: "genericCard",
    order: con.cards.length,
    visible: true,
    elements: [createPlaceholderElement()]
  });

  return newPage;
}




export function addElement(page, sectionId, containerId, cardId, newElement, index = null) {
  const newPage = JSON.parse(JSON.stringify(page));
  const card = newPage.sections
    .find(s => s.id === sectionId)?.contents
    .find(c => c.id === containerId)?.cards
    .find(cd => cd.id === cardId);

  if (!card) return page;

  // Remove placeholder if present
  if (card.elements.length === 1 && card.elements[0].type === "placeholder") {
    card.elements = [];
  }

  const el = { ...newElement, id: generateId("el") };

  if (index === null) {
    card.elements.push(el);
  } else {
    card.elements.splice(index, 0, el);
  }

  return newPage;
}
export function removeNode(page, nodeId) {
  const result = findNodeById(page, nodeId);
  if (!result) return page;

  const { level, path } = result;
  const newPage = JSON.parse(JSON.stringify(page));

  const sec = newPage.sections[path.sectionIndex];
  const con = sec?.contents[path.containerIndex];
  const card = con?.cards[path.cardIndex];

  if (level === "element") {
    card.elements.splice(path.elementIndex, 1);
    if (card.elements.length === 0) {
      card.elements.push(createPlaceholderElement());
    }
  }

  else if (level === "card") {
    con.cards.splice(path.cardIndex, 1);
    if (con.cards.length === 0) {
      // Auto-insert card with placeholder
      con.cards.push({
        id: generateId("crd"),
        componentType: "genericCard",
        order: 0,
        visible: true,
        elements: [createPlaceholderElement()]
      });
    }
  }

  else if (level === "container") {
    sec.contents.splice(path.containerIndex, 1);
    if (sec.contents.length === 0) {
      // Auto-insert new container
      return addContainer(newPage, sec.id);
    }
  }

  return newPage;
}

export function duplicateNode(page, nodeId) {
  const result = findNodeById(page, nodeId);
  if (!result) return page;

  const newPage = JSON.parse(JSON.stringify(page));
  const { level, path } = result;

  const secArr = newPage.sections;
  const sec = secArr[path.sectionIndex];
  const conArr = sec?.contents;
  const con = conArr?.[path.containerIndex];
  const cardArr = con?.cards;
  const card = cardArr?.[path.cardIndex];

  let clone;

  switch (level) {
    case "element": {
      clone = JSON.parse(JSON.stringify(card.elements[path.elementIndex]));
      clone.id = generateId("el");
      card.elements.splice(path.elementIndex + 1, 0, clone);
      break;
    }

    case "card": {
      clone = JSON.parse(JSON.stringify(card));
      clone.id = generateId("crd");

      // refresh all element IDs
      clone.elements = clone.elements.map(el => ({
        ...el,
        id: generateId("el")
      }));

      cardArr.splice(path.cardIndex + 1, 0, clone);

      // Normalize card order
      cardArr.forEach((c, i) => (c.order = i));
      break;
    }

    case "container": {
      clone = JSON.parse(JSON.stringify(con));
      clone.id = generateId("con");

      // refresh card + element IDs inside
      clone.cards = clone.cards.map(c => ({
        ...c,
        id: generateId("crd"),
        elements: c.elements.map(el => ({
          ...el,
          id: generateId("el")
        }))
      }));

      conArr.splice(path.containerIndex + 1, 0, clone);
      break;
    }

    case "section": {
      clone = JSON.parse(JSON.stringify(sec));
      clone.id = generateId("sec");

      // new IDs for entire subtree
      clone.contents = clone.contents.map(c => ({
        ...c,
        id: generateId("con"),
        cards: c.cards.map(cd => ({
          ...cd,
          id: generateId("crd"),
          elements: cd.elements.map(el => ({
            ...el,
            id: generateId("el")
          }))
        }))
      }));

      secArr.splice(path.sectionIndex + 1, 0, clone);
      break;
    }

    default:
      return page;
  }

  return newPage;
}

export function moveNode(page, nodeId, direction) {
  const result = findNodeById(page, nodeId);
  if (!result) return page;

  const { level, path } = result;
  const newPage = JSON.parse(JSON.stringify(page));

  const swap = (array, i, j) => {
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  };

  const directions = {
    up: -1,
    down: 1,
    left: -1,
    right: 1,
  };

  const d = directions[direction] ?? 0;

  // ELEMENT MOVE
  if (level === "element") {
    const elArr =
      newPage.sections[path.sectionIndex].contents[path.containerIndex].cards[path.cardIndex]
        .elements;

    const newIndex = path.elementIndex + d;
    if (newIndex >= 0 && newIndex < elArr.length) {
      swap(elArr, path.elementIndex, newIndex);
    }
  }

  // CARD MOVE
  if (level === "card") {
    const cardArr =
      newPage.sections[path.sectionIndex].contents[path.containerIndex].cards;

    const newIndex = path.cardIndex + d;
    if (newIndex >= 0 && newIndex < cardArr.length) {
      swap(cardArr, path.cardIndex, newIndex);
    }

    // normalize
    cardArr.forEach((c, i) => (c.order = i));
  }

  // CONTAINER MOVE
  if (level === "container") {
    const containerArr = newPage.sections[path.sectionIndex].contents;

    const newIndex = path.containerIndex + d;
    if (newIndex >= 0 && newIndex < containerArr.length) {
      swap(containerArr, path.containerIndex, newIndex);
    }
  }

  // SECTION MOVE
  if (level === "section") {
    const secArr = newPage.sections;

    const newIndex = path.sectionIndex + d;
    if (newIndex >= 0 && newIndex < secArr.length) {
      swap(secArr, path.sectionIndex, newIndex);
    }
  }

  return newPage;
}

export function addCardFromTemplate(page, sectionId, containerId, template, index = null) {
  const newPage = JSON.parse(JSON.stringify(page));
  const con = newPage.sections.find(s => s.id === sectionId)
    ?.contents.find(c => c.id === containerId);

  if (!con) return page;

  const newCard = {
    id: generateId("crd"),
    componentType: template.componentType,
    order: index ?? con.cards.length,
    visible: true,
    elements: template.elements.map(e => ({ ...e, id: generateId("el") })),
    responsive:template.responsive
  };

  if (index === null) con.cards.push(newCard);
  else con.cards.splice(index, 0, newCard);

  return newPage;
}


export function addContainerFromTemplate(page, sectionId, columns, index = null) {
  const newPage = JSON.parse(JSON.stringify(page));
  const sec = newPage.sections.find(s => s.id === sectionId);
  if (!sec) return page;

  const newContainer = {
    id: generateId("con"),
    layoutRole: "grid",
    columns,
    visible: true,responsive:sec.contents.responsive,
    order: index !== null ? index : sec.contents.length,
    cards: Array.from({ length: columns }).map((_, i) => ({
      id: generateId("crd"),
      componentType: "genericCard",
      order: i,
      visible: true,
      elements: [
        createPlaceholderElement() // 👈 Always add one placeholder
      ]
    }))
  };

  if (index === null) sec.contents.push(newContainer);
  else sec.contents.splice(index, 0, newContainer);

  return newPage;
}

